import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../hooks/useAuthContext';
import { useLogout } from '../hooks/useLogout';
import './MaskHistory.css';

// GitHub-style activity grid of the current user's saved masks: one square per day,
// white when nothing was saved, darker the more masks were saved. Click a day to list its
// masks; click a mask to see the saved binary mask.

const TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const pad = (n) => String(n).padStart(2, '0');
const dayKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const todayKey = () => dayKey(new Date());

// Long (e.g. encrypted) names are shortened in the middle; the full name is in the tooltip.
const shortName = (name, max = 48) => (name.length <= max ? name : `${name.slice(0, 22)}…${name.slice(-22)}`);

const formatDay = (key) => new Date(`${key}T12:00:00`).toLocaleDateString(undefined, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
});

// Columns of weeks (Sunday first, like GitHub) covering the whole year.
const buildYearWeeks = (year) => {
    const start = new Date(year, 0, 1);
    start.setDate(start.getDate() - start.getDay());
    const end = new Date(year, 11, 31);
    const weeks = [];
    for (let d = new Date(start); d <= end || d.getDay() !== 0; d.setDate(d.getDate() + 1)) {
        if (d.getDay() === 0) weeks.push([]);
        weeks[weeks.length - 1].push(d.getFullYear() === year ? dayKey(d) : null);
    }
    return weeks;
};

// 0 = none, 1-4 = quartiles of the year's non-zero days (as GitHub does).
const levelFor = (count, thresholds) => {
    if (!count) return 0;
    if (count <= thresholds[0]) return 1;
    if (count <= thresholds[1]) return 2;
    if (count <= thresholds[2]) return 3;
    return 4;
};

const MaskHistory = () => {
    const { user, expireSession } = useAuthContext();
    const { logout } = useLogout();
    const [days, setDays] = useState(null); // { 'YYYY-MM-DD': count }
    const [year, setYear] = useState(new Date().getFullYear());
    const [selectedDay, setSelectedDay] = useState(null);
    const [dayMasks, setDayMasks] = useState(null);
    const [selectedMask, setSelectedMask] = useState(null); // { id, ...meta, imageData }
    const [error, setError] = useState('');

    const api = useCallback(async (url) => {
        const res = await fetch(url, { headers: { Authorization: `Bearer ${user.token}` } });
        if (res.status === 401 || res.status === 403) {
            expireSession(); // back to the login page
            throw new Error('Your login has expired');
        }
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || res.statusText);
        return data;
    }, [user.token, expireSession]);

    useEffect(() => {
        api(`/api/annotations/history/days?tz=${encodeURIComponent(TIME_ZONE)}`)
            .then((data) => {
                setDays(data.days || {});
                // Open on the most recent year with activity.
                const keys = Object.keys(data.days || {}).sort();
                if (keys.length) setYear(Number(keys[keys.length - 1].slice(0, 4)));
            })
            .catch((e) => setError(e.message));
    }, [api]);

    const selectDay = async (key) => {
        setSelectedDay(key);
        setDayMasks(null);
        setSelectedMask(null);
        if (!(days || {})[key]) { setDayMasks([]); return; }
        try {
            const data = await api(`/api/annotations/history/day/${key}?tz=${encodeURIComponent(TIME_ZONE)}`);
            setDayMasks(data.masks);
        } catch (e) {
            setError(e.message);
        }
    };

    const selectMask = async (id) => {
        setSelectedMask({ id, loading: true });
        try {
            const data = await api(`/api/annotations/history/record/${id}`);
            setSelectedMask({ id, ...data });
        } catch (e) {
            setSelectedMask(null);
            setError(e.message);
        }
    };

    const years = useMemo(() => {
        const ys = new Set(Object.keys(days || {}).map((k) => Number(k.slice(0, 4))));
        ys.add(new Date().getFullYear());
        return [...ys].sort();
    }, [days]);

    const weeks = useMemo(() => buildYearWeeks(year), [year]);

    const { thresholds, yearTotal, activeDays } = useMemo(() => {
        const counts = Object.entries(days || {})
            .filter(([k]) => k.startsWith(`${year}-`))
            .map(([, c]) => c)
            .sort((a, b) => a - b);
        const q = (p) => counts[Math.min(counts.length - 1, Math.floor(p * counts.length))] || 0;
        return { thresholds: [q(0.25), q(0.5), q(0.75)], yearTotal: counts.reduce((a, b) => a + b, 0), activeDays: counts.length };
    }, [days, year]);

    // Month labels above the first week column that contains the 1st of that month.
    const monthLabels = useMemo(() => weeks.map((w) => {
        const first = w.find((k) => k && k.endsWith('-01'));
        return first ? MONTHS[Number(first.slice(5, 7)) - 1] : '';
    }), [weeks]);

    const today = todayKey();

    return (
        <div className="history-page">
            <header className="history-header">
                <h1>Mask history</h1>
                <div className="history-header-actions">
                    <Link className="history-btn" to="/">Annotator</Link>
                    <button className="history-btn" onClick={logout}>Logout</button>
                </div>
            </header>

            {error && <div className="history-error">{error}</div>}

            <main className="history-main">
                <section className="history-card">
                    <div className="history-card-top">
                        <div className="history-summary">
                            {days === null ? 'Loading…' : `${yearTotal} masks saved on ${activeDays} day${activeDays === 1 ? '' : 's'} in ${year}`}
                        </div>
                        <div className="history-years">
                            {years.map((y) => (
                                <button key={y} className={`history-year${y === year ? ' active' : ''}`} onClick={() => setYear(y)}>{y}</button>
                            ))}
                        </div>
                    </div>

                    <div className="history-graph-scroll">
                        <div className="history-graph">
                            <div className="history-weekdays">
                                {['', 'Mon', '', 'Wed', '', 'Fri', ''].map((d, i) => <span key={i}>{d}</span>)}
                            </div>
                            <div>
                                <div className="history-months">
                                    {monthLabels.map((m, i) => <span key={i}>{m}</span>)}
                                </div>
                                <div className="history-weeks">
                                    {weeks.map((week, wi) => (
                                        <div className="history-week" key={wi}>
                                            {week.map((key, di) => {
                                                if (!key) return <span key={di} className="history-day empty" />;
                                                const count = (days && days[key]) || 0;
                                                return (
                                                    <button
                                                        key={key}
                                                        className={`history-day level-${levelFor(count, thresholds)}${key === selectedDay ? ' selected' : ''}${key > today ? ' future' : ''}`}
                                                        title={`${count || 'No'} mask${count === 1 ? '' : 's'} on ${formatDay(key)}`}
                                                        onClick={() => selectDay(key)}
                                                        disabled={key > today}
                                                        aria-label={`${formatDay(key)}: ${count} masks`}
                                                    />
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="history-legend">
                        Less
                        {[0, 1, 2, 3, 4].map((l) => <span key={l} className={`history-day level-${l}`} />)}
                        More
                    </div>
                </section>

                {selectedDay && (
                    <section className="history-details">
                        <div className="history-list">
                            <h2>{formatDay(selectedDay)}</h2>
                            {dayMasks === null && <p className="history-muted">Loading…</p>}
                            {dayMasks && dayMasks.length === 0 && <p className="history-muted">No masks saved on this day.</p>}
                            {dayMasks && dayMasks.length > 0 && (
                                <>
                                    <p className="history-muted">{dayMasks.length} mask{dayMasks.length === 1 ? '' : 's'} — tap one to view it</p>
                                    <ul>
                                        {dayMasks.map((m) => (
                                            <li key={m.id}>
                                                <button
                                                    className={`history-item${selectedMask && selectedMask.id === m.id ? ' active' : ''}`}
                                                    onClick={() => selectMask(m.id)}
                                                    title={m.imageName}
                                                >
                                                    <span className="history-time">
                                                        {new Date(m.createdAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                                                    </span>
                                                    <span className="history-name">{shortName(m.imageName)}</span>
                                                    <span className="history-tags">
                                                        v{m.version}
                                                        {m.superseded && <span className="history-tag" title="A newer save of this image exists">older</span>}
                                                    </span>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}
                        </div>

                        <div className="history-viewer">
                            {!selectedMask && <p className="history-muted">Select a mask to see it here.</p>}
                            {selectedMask && selectedMask.loading && <p className="history-muted">Loading mask…</p>}
                            {selectedMask && selectedMask.imageData && (
                                <>
                                    <img
                                        className="history-mask"
                                        src={`data:image/png;base64,${selectedMask.imageData}`}
                                        alt={`Binary mask for ${selectedMask.imageName}`}
                                    />
                                    <div className="history-mask-meta">
                                        <div className="history-mask-name" title={selectedMask.imageName}>{selectedMask.imageName}</div>
                                        <div className="history-muted">
                                            Version {selectedMask.version} · saved {new Date(selectedMask.createdAt).toLocaleString()} · white = masked
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </section>
                )}
            </main>
        </div>
    );
};

export default MaskHistory;
